//-- Copyright (c) 2021 TMT Insights Group, LLC
import {
  addDays,
  endOfDay,
  parse,
  isBefore,
  format,
  formatDuration,
  intervalToDuration,
  isSaturday,
  nextSaturday,
  isSameDay,
  startOfDay,
  subDays
} from 'date-fns';
import { Timezone, Timezones } from '@/constants/timezones';

export function sortDates(array: string[], format: string): string[] {
  return array.sort((a, b) => {
    return isBefore(parseDate(a, format), parseDate(b, format)) ? -1 : 1;
  });
}

const DateStringFormat = 'yyyy-MM-dd';
/**
 * Converts string date into Date object.
 * Default string format = "yyyy-MM-dd"
 * @param {string} date
 * @param {string} [format]
 * @returns
 */
export function parseDate(date: string, format: string = DateStringFormat): Date {
  return parse(date, format, new Date());
}

/**
 * Converts string date into pretty string "11/21/2021"
 * @param {string} date
 * @param {string} [formatStr]
 * @returns
 */
export function formatDate(date: string | null, formatStr?: string | null) {
  if (!date) return '-';
  const lsDate = localStorage.getItem('date_format');
  const userPrefDateFormat = formatStr || (lsDate && lsDate.replace('mm', 'MM')) || 'MM/dd/yyyy';
  return format(Date.parse(date), userPrefDateFormat);
}

export function formatTime(date: string, formatStr: string = 'hh:mm aa') {
  if (!date) return '-';
  let lsTime = localStorage.getItem('time_format');
  if (lsTime && lsTime.includes('a')) {
    // this can be removed, it is to resolve a temporary bug that existed
    // only on dev and is saved only to a couple of QA testers profiles
    lsTime = 'hh:mm aa';
    localStorage.setItem('time_format', lsTime);
  }
  const userPrefTimeFormat = lsTime && !lsTime.includes('aa') ? lsTime.replace('hh', 'HH') : formatStr;
  return format(Date.parse(date), userPrefTimeFormat);
}

const distanceTokens = {
  xYears: '{{count}}y',
  xMonths: '{{count}}mo',
  xDays: '{{count}}d',
  xHours: '{{count}}h',
  aboutXHours: '{{count}}h',
  xMinutes: '{{count}}m',
  xSeconds: '{{count}}s'
};

export const formatDistanceLocale = {
  formatDistance: (token: string, count: string) => distanceTokens[token as keyof typeof distanceTokens]?.replace('{{count}}', count)
};

export const convertSecondsToDuration = (seconds: number): string => {
  const duration = intervalToDuration({ start: 0, end: seconds * 1000 });
  if (
    seconds < 60 ||
    (duration.years === 0 && duration.months === 0 && duration.days === 0 && duration.hours === 0 && duration.minutes && duration.minutes < 1)
  ) {
    return 'Just now';
  }
  let format = ['hours', 'minutes'];
  if (duration.years) {
    format = ['years', 'months'];
  } else if (duration.months) {
    format = ['months', 'days'];
  } else if (duration.days) {
    format = ['days', 'hours'];
  }
  return formatDuration(duration, { format, locale: formatDistanceLocale });
};

export const getUserTimezone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone;
export const getUserTimezoneOffset = (): number => new Date().getTimezoneOffset() / 60;
export const getUserUserTimezoneAbbr = (): string => new Date().toLocaleDateString('en-US', { day: '2-digit', timeZoneName: 'short' }).slice(4);

export const getLocalTimezone = (): Timezone | undefined => {
  const localTz = getUserTimezone();
  const localTzAbbr = getUserUserTimezoneAbbr();
  let matchedTimezone = Timezones.find((tz) => tz.utc.includes(localTz));
  if (!matchedTimezone) matchedTimezone = Timezones.find((tz) => tz.abbr == localTzAbbr);
  return matchedTimezone;
};

export const getAgeFromDate = (date: string): string | null => {
  if (!date) return null;
  const utcDate = date[date.length - 1] !== 'Z' ? date + 'Z' : date;
  const now = +new Date() / 1000;
  return convertSecondsToDuration(now - +new Date(utcDate) / 1000);
};

export const formatRuntime = (seconds: string | number): string => {
  const d = Number(seconds);
  const h = Math.floor(d / 3600);
  const m = Math.floor((d % 3600) / 60);
  const s = Math.floor((d % 3600) % 60);

  const hDisplay = h > 0 ? `${h}h` : '';
  const mDisplay = m > 0 ? `${m}m` : '';
  const sDisplay = s > 0 ? `${s}s` : '';
  return hDisplay + mDisplay + sDisplay;
};

export const roundMinutes = (date: Date): Date => {
  // If has minutes, use next hour. Reset seconds and milliseconds.
  if (date.getMinutes()) {
    date.setHours(date.getHours() + 1);
  } else {
    date.setHours(date.getHours());
  }
  date.setMinutes(0, 0, 0);
  return date;
};

export const formatDateBeginOrEnd = (date: Date, mode: 'start' | 'end') => {
  let time = 'T00:00:00.000Z';
  if (mode === 'end') {
    time = 'T23:59:59.999Z';
  }
  return `${format(date, 'yyyy-MM-dd')}${time}`;
};

const ALT_DATE_FORMATS = ['Europe', 'Asia'];
export const getIsAltDateFormat = (): boolean => {
  const userTimezone = getUserTimezone();
  return !!ALT_DATE_FORMATS.find((tz) => userTimezone.includes(tz));
};

export const addDueDateOffset = (dueDateOffsetDays: number) => {
  const millisecondsValue = 24 * 60 * 60 * 1000 * dueDateOffsetDays;

  const currentDate = new Date();
  currentDate.setTime(currentDate.getTime() + millisecondsValue);

  return currentDate;
};

export const findandFormatUTCDateInString = (str?: string, includeTime?: boolean) => {
  if (!str) return str;
  const regex = /\d{4}(\d{2})(\d{2})T(\d{2}):(\d{2}):(\d{2})/g;
  const dashRegex = /\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}\.\d{6}/g;
  const matches = str.match(regex);
  const dashmatches = str.match(dashRegex);
  let formattedString = str;

  if ((!matches || matches.length === 0) && (!dashmatches || dashmatches.length === 0)) {
    return str;
  }

  matches?.forEach((match) => {
    const isoFormattedStr = match.replace(/(\d{4})(\d{2})(\d{2})T/, '$1-$2-$3T');
    const formattedDate = formatDate(isoFormattedStr) + (includeTime ? ` ${formatTime(isoFormattedStr)}` : '');
    formattedString = formattedString.replace(match, formattedDate);
  });

  dashmatches?.forEach((match) => {
    const isoFormattedStr = match.replace(/(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})\.(\d{6})/, '$1-$2-$3T$4:$5:$6.$7');
    const formattedDate = formatDate(isoFormattedStr) + (includeTime ? ` ${formatTime(isoFormattedStr)}` : '');
    formattedString = formattedString.replace(match, formattedDate);
  });

  return formattedString;
};

/**
 * Returns the date of the most recent Sunday (including today if it's Sunday).
 *
 * @param date - The reference date (defaults to the current date).
 * @returns A Date object representing the last Sunday at midnight.
 *
 * Example:
 *  - If today is Wednesday, Oct 23 → returns Sunday, Oct 20.
 *  - If today is Sunday, Oct 27 → returns Sunday, Oct 27.
 */
export const getMostRecentSunday = (date = new Date()) => {
  const d = new Date(date);
  const day = d.getDay(); // Sunday = 0, Monday = 1, ...
  const diff = day === 0 ? 0 : day; // how many days since last Sunday
  d.setDate(d.getDate() - diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

export const getUpcomingSaturday = (date = new Date()) => {
  return isSaturday(date) ? date : nextSaturday(date);
};

export const getMonthsFirstDay = (date = new Date()) => {
  const d = new Date(date);
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d;
};

export const getStartOfWeek = () => {
  const start = new Date();
  start.setDate(start.getDate() - start.getDay());
  start.setHours(0, 0, 0, 0);
  return start;
};

export const getEndOfWeek = () => {
  const end = new Date(getStartOfWeek());
  end.setDate(end.getDate() + 5);
  end.setHours(23, 59, 59, 999);
  return end;
};

export const getDefaultDueDateRange = (now: Date = new Date()) => ({
  due_date__gte: startOfDay(subDays(now, 7)).toISOString(),
  due_date__lte: endOfDay(addDays(now, 14)).toISOString()
});

export const CALENDAR_START_DATE_FORMAT = 'yyyy-MM-dd'; // this is the format in which we store that start date in the URL query

export const parseStartDateQueryParam = (dateStr: string) => parse(dateStr, CALENDAR_START_DATE_FORMAT, new Date());

export const filterEmbargoMinAndMaxTime = (selectedDate: Date, dueDate?: string | Date) => {
  if (!dueDate) return false;

  const today = new Date();
  const due = typeof dueDate === 'string' ? new Date(dueDate) : dueDate;
  if (isSameDay(due, selectedDate)) {
    if (isSameDay(today, due)) return today.getTime() <= selectedDate.getTime() && selectedDate.getTime() <= due.getTime();
    return selectedDate.getTime() <= due.getTime();
  }

  if (isSameDay(today, selectedDate)) {
    return today.getTime() <= selectedDate.getTime();
  }

  return true;
};

export const getAgeFromCreatedAndCompleted = (createdAt: string, completedAt?: string | null): string | null => {
  if (!createdAt) return null;

  const startDate = createdAt.endsWith('Z') ? createdAt : `${createdAt}Z`;
  const endDate = completedAt ? (completedAt.endsWith('Z') ? completedAt : `${completedAt}Z`) : new Date().toISOString();
  const ageInSeconds = Math.max(0, (+new Date(endDate) - +new Date(startDate)) / 1000);

  if (ageInSeconds < 60) return `${Math.max(1, Math.floor(ageInSeconds))}s`;

  return convertSecondsToDuration(ageInSeconds);
};
